import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { confirm } from '@/lib/confirm';
import { FiChevronDown } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import usePermission from '@/hooks/usePermission';
import { useTranslation } from 'react-i18next';
import useAuthorityLevel from '@/hooks/useAuthorityLevel';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { useFormik } from 'formik';
import { IconVerified } from '@/components/custom/icon';
import { useSession } from '@/hooks/useSession';
import IconExercises from '../icons/IconExercises';
import { ButtonGroup } from '@/components/ui/button-group';
import { Button } from '@/components/ui/button';
import { DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Dropdown } from '../dropdown/dropdown';
import { API_DATETIME_FORMAT, isScheduledPublication, SchedulePublicationModal, useCanSchedulePublication } from '@/components/custom/schedule-publication-modal';

export const QuestionBookCard = props => {
  const { t } = useTranslation();
  const { questionBook, deleteQuestionBook } = props;
  const client = useClient();
  const currentUser = useSession().session.user;
  const authorityLevel = useAuthorityLevel();
  const canSchedule = useCanSchedulePublication();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [publishedAt, setPublishedAt] = useState(questionBook['published-at']);

  const form = useFormik({
    initialValues: {
      id: questionBook.id,
      published: questionBook.published
    }
  });

  const scheduled = isScheduledPublication({ published: form.values.published, 'published-at': publishedAt });

  const scheduleQBPublication = async dateTime => {
    setIsScheduling(true);
    const { error } = await client.mutate(['question_books', questionBook.id], {
      published: true,
      'published-at': dateTime
    });
    setIsScheduling(false);
    if (error) {
      toast.error(error.title || t('toast.errorPublishBook'));
      return;
    }
    form.setFieldValue('published', true);
    setPublishedAt(moment(dateTime, API_DATETIME_FORMAT).toISOString());
    setShowScheduleModal(false);
    toast.success(`Publicação agendada para ${moment(dateTime, API_DATETIME_FORMAT).format('DD/MM/YYYY [às] HH:mm')}`);
  };

  // Cancelar = despublicar e devolver o published_at para agora (senão uma nova publicação reaproveitaria a data futura).
  const cancelQBSchedule = async () => {
    const now = moment();
    const { error } = await client.mutate(['question_books', questionBook.id], {
      published: false,
      'published-at': now.format(API_DATETIME_FORMAT)
    });
    if (error) {
      toast.error(error.title || t('toast.errorPublishBook'));
      return;
    }
    form.setFieldValue('published', false);
    setPublishedAt(now.toISOString());
    toast.success('Agendamento cancelado');
  };

  const handleDelete = async id => {
    const confirmed = await confirm({
      title: t('projects.warningDeleteQuestionBook'),
      confirmText: t('button.yes'),
      cancelText: t('button.no')
    });
    if (confirmed) {
      deleteQuestionBook(id);
    }
  };

  const updateQBPublicity = async values => {
    const { error } = await client.mutate(['question_books', questionBook.id], {
      published: !values.published
    });
    if (error) {
      form.setFieldValue('published', values.published);
      toast.error(error.title || t('toast.errorPublishBook'));
    } else {
      if (!values.published === true) {
        toast.success(t('toast.successPublish'));
      } else {
        toast.success(t('toast.successUnpublish'));
      }
    }
  };

  const shouldRenderOption = useMemo(() => {
    if (questionBook?.['authored-by-user'] === true) {
      if (questionBook?.creator?.id === currentUser.id || currentUser?.profileNames?.includes('Administrador')) {
        return true;
      } else return false;
    }

    if (questionBook?.['authored-by-user'] === false) {
      if (usePermission('Editar módulos, disciplinas e aulas', 'Cursos') || usePermission('Excluir módulos, disciplinas e aulas', 'Cursos')) {
        return true;
      } else return false;
    }
  }, [questionBook]);

  return (
    <div className="card">
      {authorityLevel === 'admin' && (
        <div className="form__check form__switch">
          <input
            className="form__check-input"
            id={`publish-${form.values.id}`}
            name={`publish-${form.values.id}`}
            type="checkbox"
            checked={form.values.published}
            onClick={() => {
              form.setFieldValue('published', !form.values.published);
              updateQBPublicity(form.values);
            }}
          />
          <label htmlFor={`publish-${form.values.id}`}>{t('exams.publish')}</label>
        </div>
      )}

      <div className="card__container">
        <span className="card__icon-wrapper card__icon-wrapper--sm">
          {questionBook?.['official-content'] && <IconVerified />}
          <IconExercises className="card__icon card__icon--md" />
        </span>

        <div className="card__content">
          <div className="card__header">
            <div className="card__inner-header">
              <h2
                className="card__title"
                title={questionBook?.title}
              >
                {questionBook?.title}
              </h2>
            </div>

            {scheduled && <span className="badge badge--tiny badge--warning">Agendado para {moment(publishedAt).format('DD/MM/YYYY [às] HH:mm')}</span>}
          </div>

          <div className="avaliation-card__body">
            <p className="avaliation-card__info">
              {questionBook['question-count']}
              <span className="avaliation-card__name"> questões</span>
            </p>

            <p className="avaliation-card__info">{questionBook['t-difficulty']}</p>
          </div>
        </div>
      </div>

      <div className="card__footer">
        <ButtonGroup className="w-full">
          <Button
            variant="outline"
            size="sm"
            fullWidth
            asChild
          >
            <Link
              to={`/apostilas/gerenciar/${questionBook.id}`}
              className="btn btn--outline btn--small"
            >
              {t('button.manage')}
            </Link>
          </Button>
          {(shouldRenderOption || canSchedule) && (
            <Dropdown>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                >
                  <FiChevronDown size={20} />
                </Button>
              </DropdownMenuTrigger>

              <Dropdown.Content align="end">
                {canSchedule && <Dropdown.Item onClick={() => setShowScheduleModal(true)}>{scheduled ? 'Reagendar publicação' : 'Agendar publicação'}</Dropdown.Item>}
                {canSchedule && scheduled && <Dropdown.Item onClick={cancelQBSchedule}>Cancelar agendamento</Dropdown.Item>}
                {usePermission('Editar módulos, disciplinas e aulas', 'Cursos') && <Dropdown.Item to={`/apostilas/criar/nome/${questionBook.id}`}>{t('button.edit')}</Dropdown.Item>}
                {usePermission('Excluir módulos, disciplinas e aulas', 'Cursos') && <Dropdown.Item onClick={() => handleDelete(questionBook.id)}>{t('button.delete')}</Dropdown.Item>}
              </Dropdown.Content>
            </Dropdown>
          )}
        </ButtonGroup>
      </div>

      {canSchedule && (
        <SchedulePublicationModal
          show={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          onConfirm={scheduleQBPublication}
          initialValue={scheduled ? publishedAt : undefined}
          isSubmitting={isScheduling}
        />
      )}
    </div>
  );
};
